/**
 * RedirectParametersSection Component
 *
 * Accordion section for mapping parameters to redirect URL.
 * Part of the "DEFINE → USE" pattern: uses defined parameters in redirect destination.
 *
 * Features:
 * - 2-field pattern for each parameter (Source Type → Source Item)
 * - Parameters are added to redirect URL using their internal key
 * - The output name is automatically the parameter's internal key (defined in sources)
 *
 * @module webplacements/components/editors/destinations
 */

import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { ParameterDefinition } from '@modules/webplacements/types/webplacements.types';
import { DestinationParameterRow, type DestinationParameterMapping } from './DestinationParameterRow';

export interface RedirectParametersSectionProps {
  /** All defined parameters from all sources */
  availableParameters: ParameterDefinition[];
  /** Current redirect parameter mappings */
  mappings: DestinationParameterMapping[];
  /** Called when mappings change */
  onChange: (mappings: DestinationParameterMapping[]) => void;
  /** Base redirect URL for preview */
  redirectUrl?: string;
  /** Whether to URL encode values */
  urlEncode?: boolean;
  /** Whether the editor is disabled */
  disabled?: boolean;
}

/**
 * RedirectParametersSection Component
 *
 * Provides an interface for selecting which parameters to include in redirect URL.
 */
export const RedirectParametersSection: React.FC<RedirectParametersSectionProps> = ({
  availableParameters,
  mappings,
  onChange,
  redirectUrl,
  urlEncode = false,
  disabled = false,
}) => {
  const theme = useTheme();

  const handleAddMapping = useCallback(() => {
    if (availableParameters.length === 0) return;

    // Find first parameter not already mapped
    const existingKeys = new Set(mappings.map((m) => m.sourceKey));
    const firstAvailable = availableParameters.find((p) => !existingKeys.has(p.key));
    const param = firstAvailable || availableParameters[0];

    const newMapping: DestinationParameterMapping = {
      sourceType: param.source,
      sourceKey: param.key,
    };

    onChange([...mappings, newMapping]);
  }, [availableParameters, mappings, onChange]);

  const handleMappingChange = useCallback(
    (index: number, mapping: DestinationParameterMapping) => {
      const newMappings = [...mappings];
      newMappings[index] = mapping;
      onChange(newMappings);
    },
    [mappings, onChange]
  );

  const handleRemoveMapping = useCallback(
    (index: number) => {
      onChange(mappings.filter((_, i) => i !== index));
    },
    [mappings, onChange]
  );

  // Generate preview URL with mapped parameters
  // Output name is the same as sourceKey (defined in source sections)
  const previewParams = mappings
    .filter((m) => m.sourceKey)
    .map((m) => {
      const value = urlEncode ? encodeURIComponent('sample_value') : 'sample_value';
      return `${m.sourceKey}=${value}`;
    })
    .join('&');

  const previewUrl = redirectUrl
    ? redirectUrl + (redirectUrl.includes('?') ? '&' : '?') + previewParams
    : previewParams
    ? `?${previewParams}`
    : '';

  return (
    <Box>
      {/* Header with Add Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <OpenInNewIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
          <Typography variant="body2" color="text.secondary">
            Select which parameters to add to the redirect URL
          </Typography>
          <Tooltip title="Parameters added here will be appended to your redirect URL as query parameters. Each parameter uses its internal key as defined in the source sections above.">
            <InfoOutlinedIcon fontSize="small" color="action" />
          </Tooltip>
        </Box>
        {/* Add Button - top right */}
        <Button
          data-testid="wp-redirect-add-btn"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddMapping}
          disabled={disabled || availableParameters.length === 0}
        >
          Add Parameter to Redirect
        </Button>
      </Box>

      {/* Mappings List */}
      {mappings.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          {mappings.map((mapping, index) => (
            <DestinationParameterRow
              key={index}
              index={index}
              availableParameters={availableParameters}
              value={mapping}
              onChange={(m) => handleMappingChange(index, m)}
              onRemove={() => handleRemoveMapping(index)}
              disabled={disabled}
            />
          ))}
        </Paper>
      )}

      {/* Preview */}
      {previewParams && (
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            bgcolor: theme.palette.action.hover,
            borderStyle: 'dashed',
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Parameters added to redirect URL:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              wordBreak: 'break-all',
              color: theme.palette.text.primary,
            }}
          >
            {previewUrl}
          </Typography>
        </Paper>
      )}

      {/* Empty State */}
      {mappings.length === 0 && availableParameters.length === 0 && (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            bgcolor: theme.palette.action.hover,
            borderRadius: 1,
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Define parameters in the sections above first.
          </Typography>
        </Box>
      )}

      {mappings.length === 0 && availableParameters.length > 0 && (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            bgcolor: theme.palette.action.hover,
            borderRadius: 1,
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No parameters added to redirect URL yet.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default RedirectParametersSection;
